import { useState, useCallback, useRef, useEffect } from 'react';
import { searchFirebaseUsers } from '../services/usersService';
import { getProfilePictureUrl } from '../services/storageService';
import { profileFromSearchResult } from '../utils/userProfiles';

/**
 * Search Firebase users by exact uid, name or email. The search runs on
 * `search()` (Enter or a button), not per keystroke, because each search scans
 * the Firebase Auth user list on the backend. Editing the query clears the
 * previous results.
 * @returns {{ query, setQuery, results, searching, error, search }}
 *   `results` is null until a search has run for the current query.
 */
export const useUserSearch = () => {
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const setQuery = useCallback((value) => {
    setQueryState(value);
    setResults(null);
    setError(null);
  }, []);

  const search = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const requestId = ++requestIdRef.current;
    setSearching(true);
    setError(null);
    try {
      const found = await searchFirebaseUsers(trimmed, 'both');
      if (requestId !== requestIdRef.current) return;
      setResults(found.map(profileFromSearchResult));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err.message || 'Search failed');
      setResults([]);
    } finally {
      if (requestId === requestIdRef.current) setSearching(false);
    }
  }, [query]);

  return { query, setQuery, results, searching, error, search };
};

/**
 * Resolve profile picture URLs for a list of profiles.
 * @param {Array<{uid, profilePicturePath}>} users
 * @returns {{ pictureUrls: Map<string,string>, markPictureFailed: (uid) => void }}
 */
export const useProfilePictureUrls = (users) => {
  const [pictureUrls, setPictureUrls] = useState(new Map());
  const requestedRef = useRef(new Set());

  useEffect(() => {
    const needed = (users || []).filter(
      u => u.profilePicturePath && !requestedRef.current.has(u.uid)
    );
    if (needed.length === 0) return;
    needed.forEach(u => requestedRef.current.add(u.uid));
    Promise.all(needed.map(async u => ({ uid: u.uid, url: await getProfilePictureUrl(u.profilePicturePath) })))
      .then(loaded => {
        setPictureUrls(prev => {
          const next = new Map(prev);
          loaded.forEach(({ uid, url }) => { if (url) next.set(uid, url); });
          return next;
        });
      });
  }, [users]);

  const markPictureFailed = useCallback((uid) => {
    setPictureUrls(prev => {
      if (!prev.has(uid)) return prev;
      const next = new Map(prev);
      next.delete(uid);
      return next;
    });
  }, []);

  return { pictureUrls, markPictureFailed };
};
